import { IHttpClient } from '@common/clients/http/http.client.interface';
import { toCamelKeys, toSnakeKeys } from '@common/utils/format.util';
import {
  DeleteStandardQuestionDetailReq,
  DeleteStandardQuestionDetailRes,
  RawDeleteStandardQuestionDetailRes,
} from '@standard/models/delete-standard-question-detail.model';
import {
  GetScoringStandardDetailReq,
  GetScoringStandardDetailRes,
  RawGetScoringStandardDetailRes,
} from '@standard/models/get-scoring-standard-detail.model';
import {
  GetScoringStandardListReq,
  GetScoringStandardListRes,
  RawGetScoringStandardListRes,
} from '@standard/models/get-scoring-standard-list.model';
import {
  RawSaveStandardChartDataRes,
  SaveStandardChartDataReq,
  SaveStandardChartDataRes,
} from '@standard/models/save-standard-chart-data.model';
import {
  RawSaveStandardCorrectionTemplateRes,
  SaveStandardCorrectionTemplateReq,
  SaveStandardCorrectionTemplateRes,
} from '@standard/models/save-standard-correction-template.model';
import {
  RawSaveStandardQuestionBody,
  SaveStandardQuestionBody,
  SaveStandardQuestionBodyReq,
} from '@standard/models/save-standard-question-body.model';
import {
  RawSaveStandardQuestionDetailRes,
  SaveStandardQuestionDetailReq,
  SaveStandardQuestionDetailRes,
} from '@standard/models/save-standard-question-detail.model';
import {
  RawSaveStandardScoringDetailRes,
  SaveStandardScoringDetailReq,
  SaveStandardScoringDetailRes,
} from '@standard/models/save-standard-scoring-detail.model';
import IStandardRepository from './standard.repository.interface';

export default function standardRepositoryImpl(httpClient: IHttpClient): IStandardRepository {
  const getScoringStandardList = async (
    request: GetScoringStandardListReq,
    signal?: AbortSignal,
  ): Promise<GetScoringStandardListRes> => {
    const response = await httpClient.get<RawGetScoringStandardListRes>('/api/scoring_standards', {
      params: toSnakeKeys(request),
      signal,
    });

    return toCamelKeys(response);
  };

  const getScoringStandardDetail = async (
    request: GetScoringStandardDetailReq,
    signal?: AbortSignal,
  ): Promise<GetScoringStandardDetailRes> => {
    const response = await httpClient.get<RawGetScoringStandardDetailRes>(
      '/api/scoring_standards_detail',
      { params: toSnakeKeys(request), signal },
    );

    return toCamelKeys(response);
  };

  const saveStandardQuestionBody = async (
    request: SaveStandardQuestionBodyReq,
    signal?: AbortSignal,
  ): Promise<SaveStandardQuestionBody> => {
    const response = await httpClient.post<RawSaveStandardQuestionBody>(
      '/api/question_body',
      toSnakeKeys(request),
      { signal },
    );

    return toCamelKeys(response);
  };

  const saveStandardQuestionDetail = async (
    request: SaveStandardQuestionDetailReq,
    signal?: AbortSignal,
  ): Promise<SaveStandardQuestionDetailRes> => {
    const response = await httpClient.post<RawSaveStandardQuestionDetailRes>(
      '/api/question_detail',
      toSnakeKeys(request, { withNumber: true }),
      { signal },
    );

    return toCamelKeys(response);
  };

  const saveStandardScoringDetail = async (
    request: SaveStandardScoringDetailReq,
    signal?: AbortSignal,
  ): Promise<SaveStandardScoringDetailRes> => {
    const response = await httpClient.post<RawSaveStandardScoringDetailRes>(
      '/api/scoring_detail',
      toSnakeKeys(request),
      { signal },
    );

    return toCamelKeys(response);
  };

  const saveStandardCorrectionTemplate = async (
    request: SaveStandardCorrectionTemplateReq,
    signal?: AbortSignal,
  ): Promise<SaveStandardCorrectionTemplateRes> => {
    const response = await httpClient.post<RawSaveStandardCorrectionTemplateRes>(
      '/api/correction_template',
      toSnakeKeys(request),
      { signal },
    );

    return toCamelKeys(response);
  };

  const saveStandardChartData = async (
    request: SaveStandardChartDataReq,
    signal?: AbortSignal,
  ): Promise<SaveStandardChartDataRes> => {
    const response = await httpClient.post<RawSaveStandardChartDataRes>(
      '/api/chart_data',
      toSnakeKeys(request),
      { signal },
    );

    return toCamelKeys(response);
  };

  const deleteStandardQuestionDetail = async (
    request: DeleteStandardQuestionDetailReq,
  ): Promise<DeleteStandardQuestionDetailRes> => {
    const response = await httpClient.delete<RawDeleteStandardQuestionDetailRes>(
      '/api/question_detail',
      { params: toSnakeKeys(request) },
    );

    return toCamelKeys(response);
  };

  return {
    deleteStandardQuestionDetail,
    getScoringStandardDetail,
    getScoringStandardList,
    saveStandardChartData,
    saveStandardCorrectionTemplate,
    saveStandardQuestionBody,
    saveStandardQuestionDetail,
    saveStandardScoringDetail,
  };
}
